/**
 * Collection log query module — WP-S7.5.
 *
 * Provides structured access to investigation audit trail data
 * stored in the investigation_log table. Every action during collection
 * is logged: source queried, query params, result count, LLM calls,
 * validation results, state transitions.
 *
 * Queryable per investigation for:
 * - UI display (collection log panel in graph view)
 * - Export (included in investigation report)
 * - Debugging (detailed pipeline trace)
 */

import { getLogEntries } from './investigation-store';
import type { LogEntry, LogAction, InvestigationState } from './types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SynthesisLogEntry {
  timestamp: string;
  model: string;
  tokensUsed: number;
  sectionCount: number;
}

/** Summary of collection activity for a single investigation. */
export interface CollectionLogSummary {
  investigationId: string;
  totalEntries: number;
  stateTransitions: StateTransitionEntry[];
  tasksDispatched: number;
  tasksCompleted: number;
  tasksFailed: number;
  entitiesExtracted: number;
  entitiesRejected: number;
  llmCalls: LLMCallEntry[];
  errors: ErrorEntry[];
  phases: PhaseStatus[];
  synthesis: SynthesisLogEntry | null;
}

export interface StateTransitionEntry {
  timestamp: string;
  from: string;
  to: string;
}

export interface LLMCallEntry {
  timestamp: string;
  purpose: string;
  model: string;
  tokensUsed: number;
  taskId?: string;
}

export interface ErrorEntry {
  timestamp: string;
  state: string;
  error: string;
  taskId?: string;
}

/** Phase completion status for UI checklist display. */
export interface PhaseStatus {
  phase: InvestigationState;
  label: string;
  completed: boolean;
  timestamp?: string;
}

// ---------------------------------------------------------------------------
// Phase labels
// ---------------------------------------------------------------------------

const PHASE_LABELS: Record<InvestigationState, string> = {
  init: 'Initializing...',
  decompose: 'Decomposing query...',
  collect: 'Collecting data...',
  extract: 'Extracting entities...',
  validate: 'Validating results...',
  graph_build: 'Building graph...',
  corroborate: 'Corroboration pass...',
  synthesize: 'Generating assessment...',
  review: 'Complete',
  error: 'Error',
};

// ---------------------------------------------------------------------------
// Query functions
// ---------------------------------------------------------------------------

/**
 * Get a structured summary of the collection log for an investigation.
 * Used by the UI to display progress and the audit trail.
 */
export async function getCollectionLogSummary(
  investigationId: string,
): Promise<CollectionLogSummary> {
  const entries = await getLogEntries(investigationId);

  const summary: CollectionLogSummary = {
    investigationId,
    totalEntries: entries.length,
    stateTransitions: [],
    tasksDispatched: 0,
    tasksCompleted: 0,
    tasksFailed: 0,
    entitiesExtracted: 0,
    entitiesRejected: 0,
    llmCalls: [],
    errors: [],
    phases: buildPhaseStatuses(entries),
    synthesis: null,
  };

  for (const entry of entries) {
    switch (entry.action) {
      case 'state_transition': {
        const detail = entry.detail as Record<string, unknown>;
        summary.stateTransitions.push({
          timestamp: entry.timestamp,
          from: String(detail.from ?? ''),
          to: String(detail.to ?? ''),
        });
        break;
      }
      case 'task_dispatched':
        summary.tasksDispatched++;
        break;
      case 'task_completed':
        summary.tasksCompleted++;
        break;
      case 'task_failed':
        summary.tasksFailed++;
        break;
      case 'entity_extracted':
        summary.entitiesExtracted++;
        break;
      case 'entity_rejected':
        summary.entitiesRejected++;
        break;
      case 'llm_call': {
        const detail = entry.detail as Record<string, unknown>;
        summary.llmCalls.push({
          timestamp: entry.timestamp,
          purpose: String(detail.purpose ?? 'unknown'),
          model: String(detail.model ?? 'unknown'),
          tokensUsed: Number(detail.tokensUsed ?? 0),
          taskId: detail.taskId as string | undefined,
        });
        break;
      }
      case 'synthesis_complete': {
        const detail = entry.detail as Record<string, unknown>;
        summary.synthesis = {
          timestamp: entry.timestamp,
          model: String(detail.modelUsed ?? 'unknown'),
          tokensUsed: Number(detail.tokenCost ?? 0),
          sectionCount: Number(detail.sectionCount ?? 0),
        };
        break;
      }
      case 'error': {
        const detail = entry.detail as Record<string, unknown>;
        summary.errors.push({
          timestamp: entry.timestamp,
          state: entry.state,
          error: String(detail.error ?? 'Unknown error'),
          taskId: detail.taskId as string | undefined,
        });
        break;
      }
    }
  }

  return summary;
}

/**
 * Get raw log entries filtered by action type.
 */
export async function getLogEntriesByAction(
  investigationId: string,
  action: LogAction,
): Promise<LogEntry[]> {
  const entries = await getLogEntries(investigationId);
  return entries.filter(e => e.action === action);
}

/**
 * Get raw log entries filtered by state.
 */
export async function getLogEntriesByState(
  investigationId: string,
  state: InvestigationState,
): Promise<LogEntry[]> {
  const entries = await getLogEntries(investigationId);
  return entries.filter(e => e.state === state);
}

/**
 * Get total token usage from LLM call log entries.
 */
export async function getTotalTokenUsage(
  investigationId: string,
): Promise<{ total: number; byModel: Record<string, number> }> {
  const entries = await getLogEntries(investigationId);
  const byModel: Record<string, number> = {};
  let total = 0;

  for (const entry of entries) {
    if (entry.action === 'llm_call') {
      const detail = entry.detail as Record<string, unknown>;
      const tokens = Number(detail.tokensUsed ?? 0);
      const model = String(detail.model ?? 'unknown');
      total += tokens;
      byModel[model] = (byModel[model] ?? 0) + tokens;
    }
  }

  return { total, byModel };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Build phase completion statuses from log entries.
 * Used by the UI to show a checklist with green checkmarks.
 *
 * A phase is "completed" when the pipeline has moved PAST it
 * (i.e. the next phase in the sequence has been reached).
 * The terminal phase ('review') is completed when it's reached.
 */
function buildPhaseStatuses(entries: LogEntry[]): PhaseStatus[] {
  const phaseOrder: InvestigationState[] = [
    'init', 'decompose', 'collect', 'extract', 'validate',
    'graph_build', 'corroborate', 'synthesize', 'review',
  ];

  // Find which phases have been reached (via state transitions)
  const reachedPhases = new Set<string>();
  const phaseTimestamps: Record<string, string> = {};

  for (const entry of entries) {
    if (entry.action === 'state_transition') {
      const detail = entry.detail as Record<string, unknown>;
      const to = String(detail.to ?? '');
      if (to && to !== 'error') {
        reachedPhases.add(to);
        phaseTimestamps[to] = entry.timestamp;
      }
    }
  }

  return phaseOrder.map((phase, index) => {
    // A phase is completed when the next phase has been reached,
    // or for the terminal phase ('review'), when it itself is reached.
    const isTerminal = index === phaseOrder.length - 1;
    const nextPhaseReached = !isTerminal && reachedPhases.has(phaseOrder[index + 1]);
    const completed = isTerminal ? reachedPhases.has(phase) : nextPhaseReached;

    return {
      phase,
      label: PHASE_LABELS[phase],
      completed,
      timestamp: phaseTimestamps[phase],
    };
  });
}
